import React, { useReducer } from 'react'
import PermanenceReportContext from './PermanenceReportContext'
import PermanenceReportReducer from './PermanenceReportReducer'
import { t } from 'i18next'
import { downloadPermanenceReportService, getPermanenceReportService, getPermanenceTotalsReportService } from '../../services/permanceReport'
import { logout } from '../../services/login'
import { getTenantInfoByKey } from '../../helpers/helpers'

const PermanenceReportState = ({ children }) => {

    const initialState = {
        isFetching: true,
        error: false,
        message: '',
        reports: [],
        totalsReports: [],
        totalReport: null,
        isFetchingTotalsReports: true,
        reportFilter: {
            date_from: '',
            date_to: '',
            opening_code: [],
            contract_type: [],
            contractors: [],
            buildings: [],
            search_text: '',
            column_order: 'employee',
            order_type: 'ASC',
            reg_from: 1,
            page_length: 7,
        },
        headers: [
            { key: ('RESOURCE_DATA'), columnOrder: 'employee', order: 'ASC', active: true },
            { key: ('TYPE_CONTRACT'), columnOrder: 'contract_type', order: 'ASC', active: false },
            { key: ('CONT'), columnOrder: 'contractor', order: 'ASC', active: false },
            { key: ('REG_NUM') },
            { key: ('NORMAL_HS') },
            { key: ('50%_HS') },
            { key: ('100%_HS') },
            { key: '' },
        ],
        totalPages: null,
        isDownloadingReport: false,
        downloadError: false,
        downloadMessage: false
    }

    const [state, dispatch] = useReducer(PermanenceReportReducer, initialState);

    const setFilter = (newFilter) => {
        dispatch({
            type: 'SET_FILTER',
            payload: newFilter
        })
    }

    const getPermanenceReport = async (newFilter) => {
        try {
            setFilter(newFilter);

            const filter = {
                ...newFilter,
                ...(newFilter.date_from && { date_from: newFilter.date_from.replaceAll('-', '') }),
                ...(newFilter.date_to && { date_to: newFilter.date_to.replaceAll('-', '') })
            };

            const headers = state.headers.map((header) => (
                header.columnOrder === filter.column_order ?
                    {
                        ...header,
                        active: true,
                        order: filter.order_type
                    }
                    :
                    {
                        ...header,
                        ...(header.columnOrder ? { active: false, order: 'ASC' } : {})
                    }
            ));

            const response = await getPermanenceReportService(filter);

            if (response.ok) {
                dispatch({
                    type: 'GET_PERMANENCE_REPORT',
                    payload: {
                        reports: response.data.response.employees,
                        totalPages: response.data.response.total_pages,
                        headers
                    }
                })
            } else {
                const errorType = await response.data.json();
                switch (errorType.error_code) {
                    case 'AUTH_ERROR':
                        dispatch({
                            type: 'ERROR',
                            payload: t('TOKEN_ERROR')
                        })
                        setTimeout(() => {
                            logout();
                        }, 1000);
                        break;
                    case 'PROFILE_ACCESS_ERROR':
                        dispatch({
                            type: 'ERROR',
                            payload: t('PERMISSIONS_ERROR')
                        })
                        setTimeout(() => {
                            logout();
                        }, 1000);
                        break;
                    default:
                        dispatch({
                            type: 'ERROR',
                            payload: t('ERROR_TRY_AGAIN')
                        })
                        break;
                }
            }
        } catch (e) {
            console.log(e);
            dispatch({
                type: 'ERROR',
                payload: t('SERVER_ERROR')
            })
        }
    }

    const getPermanenceTotalsReport = async (newFilter) => {
        try {

            const haveBuildings = getTenantInfoByKey('is_building');
            const haveAccessOpenings = getTenantInfoByKey('is_access_opening');
            const haveBuildingsAndNoOpenings = haveAccessOpenings === false && haveBuildings === true;

            dispatch({ type: 'IS_FETCHING_TOTALS' });

            const filter = {
                ...newFilter,
                ...(newFilter.date_from && { date_from: newFilter.date_from.replaceAll('-', '') }),
                ...(newFilter.date_to && { date_to: newFilter.date_to.replaceAll('-', '') })
            };
            const response = await getPermanenceTotalsReportService(filter);

            if (response.ok) {
                const totalsReports = haveBuildingsAndNoOpenings ? response.data.response[0].buildings : response.data.response;
                const totalReport = response.data.response.reduce((prev, current) => {
                    prev.hs_normal += current.hs_normal;
                    prev.hs_50 += current.hs_50;
                    prev.hs_100 += current.hs_100;
                    prev.hs_total += current.hs_total;
                    return prev;
                },
                    { hs_normal: 0, hs_50: 0, hs_100: 0, hs_total: 0 }
                );
                dispatch({
                    type: 'GET_PERMANENCE_REPORT_TOTALS',
                    payload: {
                        totalsReports,
                        totalReport
                    }
                })
            } else {
                const errorType = await response.data.json();
                switch (errorType.error_code) {
                    case 'AUTH_ERROR':
                        dispatch({
                            type: 'ERROR_TOTALS',
                            payload: t('TOKEN_ERROR')
                        })
                        setTimeout(() => {
                            logout();
                        }, 1000);
                        break;
                    case 'PROFILE_ACCESS_ERROR':
                        dispatch({
                            type: 'ERROR_TOTALS',
                            payload: t('PERMISSIONS_ERROR')
                        })
                        setTimeout(() => {
                            logout();
                        }, 1000);
                        break;
                    default:
                        dispatch({
                            type: 'ERROR_TOTALS',
                            payload: t('ERROR_TRY_AGAIN')
                        })
                        break;
                }
            }
        } catch (e) {
            console.log(e);
            dispatch({
                type: 'ERROR_TOTALS',
                payload: t('SERVER_ERROR')
            })
        }
    }

    const handleGetPermanenceReport = (newFilter) => {
        getPermanenceReport(newFilter);
        getPermanenceTotalsReport(newFilter);
    }

    const downloadAlertHandler = ({ isDownloadingReport, downloadError, downloadMessage }) => {
        dispatch({
            type: 'DOWNLOADING',
            payload: {
                isDownloadingReport,
                downloadError,
                downloadMessage
            }
        })
    }

    const downloadPermanenceReport = async () => {
        try {
            downloadAlertHandler({ isDownloadingReport: true, downloadError: false, downloadMessage: '' });
            const filter = {
                ...state.reportFilter,
                ...(state.reportFilter.date_from && { date_from: state.reportFilter.date_from.replaceAll('-', '') }),
                ...(state.reportFilter.date_to && { date_to: state.reportFilter.date_to.replaceAll('-', '') })
            };
            const response = await downloadPermanenceReportService(filter);
            if (response.ok) {
                const link = document.createElement("a");
                link.target = "_blank";
                link.download = `${t('PERMANENCE')} ${state.reportFilter.date_from} - ${state.reportFilter.date_to}`
                link.href = URL.createObjectURL(new Blob([response.data], { type: response.data.type }));
                link.click();
                downloadAlertHandler({ isDownloadingReport: false, downloadError: false, downloadMessage: '' });
            } else {
                const errorType = await response.data.json();
                switch (errorType.error_code) {
                    case 'AUTH_ERROR':
                        downloadAlertHandler({
                            isDownloadingReport: false,
                            downloadError: true,
                            downloadMessage: t('TOKEN_ERROR')
                        });
                        setTimeout(() => {
                            logout();
                        }, 1000);
                        break;
                    case 'PROFILE_ACCESS_ERROR':
                        downloadAlertHandler({
                            isDownloadingReport: false,
                            downloadError: true,
                            downloadMessage: t('PERMISSIONS_ERROR')
                        });
                        setTimeout(() => {
                            logout();
                        }, 1000);
                        break;
                    default:
                        downloadAlertHandler({
                            isDownloadingReport: false,
                            downloadError: true,
                            downloadMessage: t('ERROR_TRY_AGAIN')
                        });
                        break;
                }
            }
        } catch (e) {
            console.log(e);
            downloadAlertHandler({
                isDownloadingReport: false,
                downloadError: true,
                downloadMessage: t('SERVER_ERROR')
            });
        }
    }

    return (
        <PermanenceReportContext.Provider
            value={{
                permanenceReportState: state,
                setFilter,
                getPermanenceReport,
                getPermanenceTotalsReport,
                handleGetPermanenceReport,
                downloadPermanenceReport,
                downloadAlertHandler
            }}
        >
            {children}
        </PermanenceReportContext.Provider>
    )
}

export default PermanenceReportState